import { CheckCircle, CircleAlert, Info, TriangleAlert, X } from "lucide-react"
import { HTMLAttributes, ReactNode, useEffect, useMemo } from "react"
import { getUserMessage, isApiError } from "shared-ts/api/apiError"
import { useUserVisibleErrorLogger } from "shared-ts/hooks/useUserVisibleErrorLogger"

import { classNames } from "../../../../lib/classNames"
import { DooeyIcon } from "../../../DooeyIcon"
import { Icon, IconProps, IconSizes } from "../../../Icon"
import {
  Button,
  ButtonMode,
  ButtonSize,
  ButtonTone,
} from "../../control/Button"

export const BannerSeverity = {
  Error: "error",
  Warning: "warning",
  Success: "success",
  Info: "info",
  AI: "ai",
} as const
export type BannerSeverity =
  (typeof BannerSeverity)[keyof typeof BannerSeverity]

export const BannerVariant = {
  Default: "default",
  Inline: "inline",
} as const
export type BannerVariant = (typeof BannerVariant)[keyof typeof BannerVariant]

export const BannerSize = {
  Default: "default",
  Small: "small",
} as const
export type BannerSize = (typeof BannerSize)[keyof typeof BannerSize]

// Paired: a label without a handler (or a handler without a label) fails to
// compile rather than silently rendering no button.
type BannerActionProps =
  | {
      // Renders a tone-matched tonal action button.
      actionLabel: string
      onAction: () => void
    }
  | { actionLabel?: never; onAction?: never }

type BannerVariantProps =
  | ({
      variant?: typeof BannerVariant.Default
      // Optional bold heading shown above the message.
      title?: string
      message: ReactNode
      // Renders a dismiss control when provided.
      onClose?: () => void
      // Logged to the user-visible error monitor when severity is error; an
      // ApiError's userMessage also overrides a string message.
      error?: unknown
      loggerScope?: string
      size?: never
    } & BannerActionProps)
  | {
      // Icon-only indicator that a collapsed surface (e.g. a cart line item)
      // carries a banner. Takes no content, action, or dismiss, and never
      // logs — the indicator is not the error surface.
      variant: typeof BannerVariant.Inline
      title?: never
      message?: never
      actionLabel?: never
      onAction?: never
      onClose?: never
      error?: never
      loggerScope?: never
      size?: BannerSize
    }

// HTMLElement rather than a concrete tag: the default variant renders a div
// and the inline variant a span, and otherProps spread onto both.
export type BannerV2Props = Omit<HTMLAttributes<HTMLElement>, "title"> & {
  severity: BannerSeverity
} & BannerVariantProps

export function BannerV2({
  severity,
  variant = BannerVariant.Default,
  title,
  message,
  actionLabel,
  onAction,
  onClose,
  error,
  loggerScope,
  size = BannerSize.Default,
  className,
  ...otherProps
}: BannerV2Props) {
  const BannerIcon = severityIcon[severity]

  /* Attempt to override a default string message with ApiError's userMessage. */
  const overriddenMessage = useMemo(() => {
    if (typeof message !== "string") return undefined

    return (isApiError(error) && getUserMessage(error)) || message
  }, [message, error])

  /* Wrap the Error locally to capture the call site stack trace. */
  const wrappedError = useMemo(() => {
    if (!error) return undefined

    const wError = new Error(
      error instanceof Error ? error.message : String(error),
      { cause: error }
    )
    if (error instanceof Error) wError.name = error.name

    return wError
  }, [error])

  const { error: logUserVisibleError, info: logUserVisibleInfo } =
    useUserVisibleErrorLogger(loggerScope ? `banner.${loggerScope}` : "banner")

  const shouldLog =
    severity === BannerSeverity.Error && variant !== BannerVariant.Inline

  useEffect(() => {
    if (!shouldLog || !wrappedError) return

    logUserVisibleError(
      wrappedError,
      overriddenMessage ?? "Unknown user error message"
    )
  }, [shouldLog, wrappedError, overriddenMessage, logUserVisibleError])

  useEffect(() => {
    if (!shouldLog || wrappedError) return

    logUserVisibleInfo(
      "Error Banner shown without an Error object",
      overriddenMessage ?? "Unknown user error message"
    )
  }, [shouldLog, wrappedError, overriddenMessage, logUserVisibleInfo])

  if (variant === BannerVariant.Inline) {
    return (
      <span
        data-testid={`${severity}-banner-inline`}
        role="img"
        aria-label={`${severity} banner`}
        className={classNames(
          "inline-flex shrink-0 items-center justify-center rounded-sm border",
          inlineContainerSizeClassName[size],
          containerClassName[severity],
          className
        )}
        {...otherProps}
      >
        <Icon
          icon={BannerIcon}
          size={inlineIconSize[size]}
          className={toneTextClassName[severity]}
        />
      </span>
    )
  }

  return (
    <div
      data-testid={`${severity}-banner`}
      role={severity === BannerSeverity.Error ? "alert" : "status"}
      className={classNames(
        "max-w-150 flex items-center gap-x-3 rounded-sm border p-2",
        containerClassName[severity],
        className
      )}
      {...otherProps}
    >
      <Icon
        icon={BannerIcon}
        size={IconSizes.Small}
        className={classNames("shrink-0", toneTextClassName[severity])}
      />
      <div className="flex-1 whitespace-pre-line">
        {title && (
          <div className="text-body-lg text-v2-foreground-default font-semibold">
            {title}
          </div>
        )}
        <div className="text-body text-v2-foreground-default">
          {overriddenMessage ?? message}
        </div>
      </div>
      {actionLabel && onAction && (
        <Button
          mode={ButtonMode.Tonal}
          tone={severityTone[severity]}
          size={ButtonSize.Small}
          onClick={onAction}
          className="shrink-0"
        >
          {actionLabel}
        </Button>
      )}
      {onClose && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onClose}
          className="text-v2-foreground-default-weak hover:text-v2-foreground-default shrink-0"
        >
          <Icon icon={X} size={IconSizes.Small} />
        </button>
      )}
    </div>
  )
}

const severityTone: Record<BannerSeverity, ButtonTone> = {
  [BannerSeverity.Error]: ButtonTone.Danger,
  [BannerSeverity.Warning]: ButtonTone.Alert,
  [BannerSeverity.Success]: ButtonTone.Success,
  [BannerSeverity.Info]: ButtonTone.Information,
  [BannerSeverity.AI]: ButtonTone.AI,
}

const inlineContainerSizeClassName: Record<BannerSize, string> = {
  [BannerSize.Default]: "h-6.5 w-7.5",
  [BannerSize.Small]: "size-5.5",
}

const inlineIconSize: Record<BannerSize, NonNullable<IconProps["size"]>> = {
  [BannerSize.Default]: IconSizes.ExtraSmall,
  [BannerSize.Small]: IconSizes.ExtraExtraSmall,
}

const severityIcon: Record<BannerSeverity, IconProps["icon"]> = {
  [BannerSeverity.Error]: CircleAlert,
  [BannerSeverity.Warning]: TriangleAlert,
  [BannerSeverity.Success]: CheckCircle,
  [BannerSeverity.Info]: Info,
  [BannerSeverity.AI]: DooeyIcon,
}

const containerClassName: Record<BannerSeverity, string> = {
  [BannerSeverity.Error]:
    "bg-v2-background-danger-weak border-v2-border-danger",
  [BannerSeverity.Warning]:
    "bg-v2-background-alert-weak border-v2-border-alert",
  [BannerSeverity.Success]:
    "bg-v2-background-success-weak border-v2-border-success",
  [BannerSeverity.Info]:
    "bg-v2-background-information-weak border-v2-border-information",
  [BannerSeverity.AI]: "bg-v2-background-ai-weak border-v2-border-ai",
}

// Tone color for the icon; title/message stay neutral in the default variant.
const toneTextClassName: Record<BannerSeverity, string> = {
  [BannerSeverity.Error]: "text-v2-foreground-danger",
  [BannerSeverity.Warning]: "text-v2-foreground-alert",
  [BannerSeverity.Success]: "text-v2-foreground-success",
  [BannerSeverity.Info]: "text-v2-foreground-information",
  [BannerSeverity.AI]: "text-v2-foreground-ai",
}
